import { z } from 'zod';

/**
 * Browser-side configuration, validated once at module load.
 *
 * The same principle as the API's env schema: a missing or malformed value fails
 * immediately and loudly instead of surfacing later as an unexplained network error
 * against `undefined/api/v1/...`.
 *
 * Only `VITE_`-prefixed variables exist in browser code — Vite inlines them at build
 * time. Nothing secret can go here: every value ends up readable in the shipped
 * bundle. That is why the API base URL is present and no API key ever is.
 */
const envSchema = z.object({
  /**
   * Defaults to a relative path, which is the correct value for both development
   * (Vite proxies `/api` to the API, keeping one origin so `SameSite` cookies work)
   * and production (one process serves the SPA and the API). An absolute URL is only
   * needed when the two are genuinely on different hosts.
   */
  apiBaseUrl: z.string().default('/api/v1'),
  siteUrl: z.string().default(''),
  mode: z.enum(['development', 'production', 'test']).default('development'),
});

function readEnv(): z.infer<typeof envSchema> {
  const raw = {
    apiBaseUrl: import.meta.env['VITE_API_BASE_URL'] as string | undefined,
    siteUrl: import.meta.env['VITE_SITE_URL'] as string | undefined,
    mode: import.meta.env.MODE,
  };

  const parsed = envSchema.safeParse({
    // Strip empty strings so a blank .env entry falls back to the default rather
    // than producing an empty base URL.
    apiBaseUrl: raw.apiBaseUrl || undefined,
    siteUrl: raw.siteUrl || undefined,
    mode: raw.mode,
  });

  if (!parsed.success) {
    throw new Error(
      `Invalid frontend configuration:\n${parsed.error.issues
        .map((issue) => `  • ${issue.path.join('.')}: ${issue.message}`)
        .join('\n')}`,
    );
  }

  return parsed.data;
}

const parsed = readEnv();

/**
 * When the API is on the same origin, prefer the relative path.
 *
 * An absolute `http://localhost:3010/api/v1` in development would bypass the Vite
 * proxy and make every request cross-origin, which silently drops the session cookie.
 * Rewriting a same-origin absolute URL to a relative one removes that footgun.
 */
function normaliseApiBaseUrl(value: string): string {
  if (!value.startsWith('http')) return value.replace(/\/+$/, '');

  try {
    const url = new URL(value);
    if (typeof window !== 'undefined' && url.origin === window.location.origin) {
      return url.pathname.replace(/\/+$/, '');
    }
    return value.replace(/\/+$/, '');
  } catch {
    return '/api/v1';
  }
}

export const env = Object.freeze({
  apiBaseUrl: normaliseApiBaseUrl(parsed.apiBaseUrl),
  siteUrl: parsed.siteUrl.replace(/\/+$/, ''),
  isDevelopment: parsed.mode === 'development',
  isProduction: parsed.mode === 'production',
  isTest: parsed.mode === 'test',
});

export type Env = typeof env;
